import { take, unique } from 'lib/array/arrayUtils'

export type HomepageCarouselDeduplication =
  | { mode: 'respect' }
  | { mode: 'allow', reason: string }

export interface HomepageOfferClaimMaps {
  claimedIds: Set<string>
  ownedByCarousel: Map<string, Set<string>>
}

interface ResolveHomepageCarouselDisplayInput {
  carouselId: string
  deduplication: HomepageCarouselDeduplication
  candidateOfferIds: Array<string>
  claimedIds: ReadonlySet<string>
  ownedByCarousel: ReadonlyMap<string, ReadonlySet<string>>
  maxDisplayCount?: number
}

export interface CommitHomepageCarouselDisplayInput {
  carouselId: string
  deduplication: HomepageCarouselDeduplication
  candidateOfferIds: Array<string>
  maxDisplayCount?: number
  minimumOffersCount?: number
  applyMinimumOffersCount: boolean
  hideWhenEmpty?: boolean
}

export function buildHomepageOfferCarouselClaimCacheKey(
  input: {
    listKey: string
    maxDisplayCount?: number
    applyMinimumOffersCount: boolean
    minimumOffersCount?: number
    hideWhenEmpty?: boolean
  },
): string {
  return [
    input.listKey,
    input.maxDisplayCount ?? '',
    input.applyMinimumOffersCount ? '1' : '0',
    input.minimumOffersCount ?? '',
    input.hideWhenEmpty ? '1' : '0',
  ].join(':')
}

export interface HomepageCarouselDisplayDecision {
  displayOfferIds: Array<string>
  hidden: boolean
}

function resolveHomepageCarouselDisplayOfferIds(
  input: ResolveHomepageCarouselDisplayInput,
): Array<string> {
  const uniqueCandidateIds = unique(input.candidateOfferIds)
  const ownedOfferIds = input.ownedByCarousel.get(input.carouselId)

  const claimableOfferIds = uniqueCandidateIds.filter((offerId) => {
    if (ownedOfferIds?.has(offerId)) {
      return true
    }

    if (!input.claimedIds.has(offerId)) {
      return true
    }

    return input.deduplication.mode === 'allow'
  })

  if (input.maxDisplayCount === undefined) {
    return claimableOfferIds
  }

  return take(claimableOfferIds, input.maxDisplayCount)
}

export function shouldHideHomepageCarouselDisplay(
  displayCount: number,
  options: {
    minimumOffersCount?: number
    applyMinimumOffersCount: boolean
    hideWhenEmpty?: boolean
  },
): boolean {
  if (options.applyMinimumOffersCount && options.minimumOffersCount && displayCount < options.minimumOffersCount) {
    return true
  }

  return !!options.hideWhenEmpty && displayCount === 0
}

interface HomepageOfferCarouselRenderState {
  isActive: boolean
  ready: boolean
  hidden: boolean
  displayOfferIds: Array<string>
}

export function withHomepageOfferCarouselDisplayOfferIds<T extends { offerIds: Array<string>, fetching?: boolean }>(
  list: T,
  renderState: HomepageOfferCarouselRenderState,
): T {
  if (!renderState.isActive) {
    return list
  }

  if (!renderState.ready && !renderState.hidden) {
    return {
      ...list,
      offerIds: renderState.displayOfferIds,
      fetching: true,
    }
  }

  if (list.offerIds === renderState.displayOfferIds) {
    return list
  }

  return {
    ...list,
    offerIds: renderState.displayOfferIds,
  }
}

export function commitHomepageCarouselDisplay(
  maps: HomepageOfferClaimMaps,
  input: CommitHomepageCarouselDisplayInput,
): HomepageCarouselDisplayDecision {
  const displayOfferIds = resolveHomepageCarouselDisplayOfferIds({
    carouselId: input.carouselId,
    deduplication: input.deduplication,
    candidateOfferIds: input.candidateOfferIds,
    claimedIds: maps.claimedIds,
    ownedByCarousel: maps.ownedByCarousel,
    maxDisplayCount: input.maxDisplayCount,
  })
  const hidden = shouldHideHomepageCarouselDisplay(displayOfferIds.length, input)

  if (hidden) {
    return {
      displayOfferIds: [],
      hidden: true,
    }
  }

  let ownedOfferIds = maps.ownedByCarousel.get(input.carouselId)
  if (!ownedOfferIds) {
    ownedOfferIds = new Set()
    maps.ownedByCarousel.set(input.carouselId, ownedOfferIds)
  }

  displayOfferIds.forEach((offerId) => {
    ownedOfferIds.add(offerId)
    maps.claimedIds.add(offerId)
  })

  return {
    displayOfferIds,
    hidden: false,
  }
}
